/**
 * Straight-line CAPEX amortisation (must match server/helpers/compV3/capexAmortization.js).
 * Fixed academic years: 1 July → 30 June.
 */

const ACADEMIC_YEAR_END_MONTH = 6;

export function computeCapexAmortizationSchedule(input) {
  const totalAmount = Number(input.totalAmount);
  const totalMonths = readTotalMonthsHorizon(input);

  if (!Number.isFinite(totalAmount) || totalAmount <= 0 || !Number.isFinite(totalMonths) || totalMonths < 1) {
    return {
      rows: [],
      monthlyAmount: 0,
      amountForFullSchoolYear: 0,
      firstPeriodMonths: 0,
    };
  }

  const monthlyAmount = totalAmount / totalMonths;
  const amountForFullSchoolYear = 12 * monthlyAmount;

  const invoiceDate = normalizeDate(input.invoiceDate);
  const invoiceMonth = calendarMonth(invoiceDate);
  const firstPeriodMonths = monthsInFirstJulyJuneSlice(invoiceMonth, ACADEMIC_YEAR_END_MONTH);
  const firstWindow = julyJuneWindowContaining(invoiceDate);

  const rows = buildRows({
    totalAmount,
    totalMonths,
    monthlyAmount,
    firstPeriodMonths,
    firstWindow,
  });

  return {
    rows,
    monthlyAmount,
    amountForFullSchoolYear,
    firstPeriodMonths,
  };
}

function readTotalMonthsHorizon(input) {
  if (input.depreciationPeriodMonths != null && input.depreciationPeriodMonths !== "") {
    const n = Math.floor(Number(input.depreciationPeriodMonths));
    if (Number.isFinite(n) && n >= 1) return n;
  }
  if (input.lifetimeYears != null && input.lifetimeYears !== "") {
    const years = Math.floor(Number(input.lifetimeYears));
    if (Number.isFinite(years) && years >= 1) return years * 12;
  }
  return 0;
}

function normalizeDate(d) {
  const dt = d instanceof Date ? d : new Date(d);
  return Number.isNaN(dt.getTime()) ? new Date() : dt;
}

function calendarMonth(date) {
  return date.getMonth() + 1;
}

function monthsInFirstJulyJuneSlice(invoiceMonth, academicYearEndMonth) {
  if (invoiceMonth > academicYearEndMonth) {
    const gapAfterJune = invoiceMonth - academicYearEndMonth;
    const monthsFromInvoiceToDecember = 12 - gapAfterJune;
    return monthsFromInvoiceToDecember + 1;
  }
  return academicYearEndMonth - invoiceMonth + 1;
}

function formatYearPairLabel(julyStart, juneEnd) {
  return `${julyStart.getFullYear()}-${juneEnd.getFullYear()}`;
}

function shiftJulyJuneWindow(start, end, yearDelta) {
  const s = new Date(start);
  const e = new Date(end);
  s.setFullYear(s.getFullYear() + yearDelta);
  e.setFullYear(e.getFullYear() + yearDelta);
  return { start: s, end: e };
}

function julyJuneWindowContaining(invoiceDate) {
  const d = normalizeDate(invoiceDate);
  const calYear = d.getFullYear();
  const month = calendarMonth(d);
  const julyCalendarYear = month >= 7 ? calYear : calYear - 1;
  const start = new Date(julyCalendarYear, 6, 1);
  const end = new Date(julyCalendarYear + 1, 5, 30);
  return { start, end };
}

function buildRows({ totalAmount, totalMonths, monthlyAmount, firstPeriodMonths, firstWindow }) {
  const rows = [];
  let monthsScheduled = 0;
  let windowIndex = 0;

  const pushRow = (monthsInRow, schoolYearLabel) => {
    rows.push({
      schoolYearLabel,
      months: monthsInRow,
      amount: monthsInRow * monthlyAmount,
    });
    monthsScheduled += monthsInRow;
  };

  pushRow(
    firstPeriodMonths,
    formatYearPairLabel(firstWindow.start, firstWindow.end)
  );

  while (totalMonths - monthsScheduled > 12) {
    windowIndex += 1;
    const { start, end } = shiftJulyJuneWindow(firstWindow.start, firstWindow.end, windowIndex);
    pushRow(12, formatYearPairLabel(start, end));
  }

  if (monthsScheduled < totalMonths) {
    windowIndex += 1;
    const remainingMonths = totalMonths - monthsScheduled;
    const { start, end } = shiftJulyJuneWindow(firstWindow.start, firstWindow.end, windowIndex);
    pushRow(remainingMonths, formatYearPairLabel(start, end));
  }

  const sumAmounts = rows.reduce((acc, r) => acc + r.amount, 0);
  const roundingDiff = totalAmount - sumAmounts;
  if (rows.length && Math.abs(roundingDiff) > 1e-9) {
    const last = rows[rows.length - 1];
    last.amount = Number((last.amount + roundingDiff).toFixed(9));
  }

  return rows;
}
